import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { finalize } from 'rxjs';
import { MaterialModule } from '../material.module';
import { LabEquipmentInstrument } from './lab-equipment.models';
import { LabEquipmentService } from './lab-equipment.service';

export type InstrumentStatusDialogMode = 'remove' | 'restore' | 'editRemoval';

export interface InstrumentStatusDialogData {
  instrument: LabEquipmentInstrument;
  mode?: InstrumentStatusDialogMode;
}

@Component({
  selector: 'app-instrument-status-dialog',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MaterialModule],
  templateUrl: './instrument-status-dialog.component.html',
  styleUrl: './instrument-status-dialog.component.scss',
})
export class InstrumentStatusDialogComponent {
  private readonly formBuilder = inject(FormBuilder);
  private readonly service = inject(LabEquipmentService);
  readonly dialogRef = inject(MatDialogRef<InstrumentStatusDialogComponent>);
  readonly data = inject<InstrumentStatusDialogData>(MAT_DIALOG_DATA);
  readonly mode: InstrumentStatusDialogMode = this.data.mode
    ?? (this.data.instrument.isDeleted ? 'restore' : 'remove');
  readonly isRestore = this.mode === 'restore';
  readonly isEditRemoval = this.mode === 'editRemoval';
  readonly form = this.formBuilder.nonNullable.group({
    effectiveDate: [this.isEditRemoval ? this.data.instrument.removalDate ?? this.today() : this.today(), Validators.required],
    reason: [
      this.isRestore
        ? 'Returned to active service'
        : this.isEditRemoval ? this.data.instrument.removalReason ?? '' : '',
      [Validators.required, Validators.maxLength(500)],
    ],
  });
  isSaving = false;
  errorMessage = '';

  save(): void {
    if (this.form.invalid || this.isSaving) {
      this.form.markAllAsTouched();
      return;
    }

    const value = this.form.getRawValue();
    const operation = this.isRestore
      ? this.service.restoreInstrument(this.data.instrument.id, value.effectiveDate, value.reason.trim())
      : this.isEditRemoval
        ? this.service.updateInstrumentRemoval(this.data.instrument.id, value.effectiveDate, value.reason.trim())
        : this.service.removeInstrument(this.data.instrument.id, value.effectiveDate, value.reason.trim());
    this.isSaving = true;
    this.errorMessage = '';
    operation.pipe(finalize(() => this.isSaving = false)).subscribe({
      next: instrument => this.dialogRef.close(instrument),
      error: error => {
        this.errorMessage = error?.error?.detail
          ?? `The instrument could not be ${this.isRestore ? 'restored' : this.isEditRemoval ? 'updated' : 'removed'}. Please retry.`;
      },
    });
  }

  private today(): string {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  }
}